/**
 * 系统配置 Store
 * 管理从API动态获取的全局配置(下载链接、播放器URL等)
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getSystemConfig } from '@/api/home'

/**
 * 系统配置接口定义
 */
export interface SystemConfig {
  download_url: string
  live_stream_url_h5: string
  live_stream_url_web: string
}

/**
 * 配置Store
 */
export const useConfigStore = defineStore('config', () => {
  // ===== State =====
  const config = ref<SystemConfig>({
    download_url: '',
    live_stream_url_h5: '',
    live_stream_url_web: ''
  })
  
  const loading = ref(false)
  const error = ref<string | null>(null)
  const initialized = ref(false)

  // ===== Getters =====
  const downloadUrl = computed(() => config.value.download_url)
  const liveStreamUrlH5 = computed(() => config.value.live_stream_url_h5)
  const liveStreamUrlWeb = computed(() => config.value.live_stream_url_web)
  
  // 检查配置是否已加载
  const isConfigReady = computed(() => initialized.value && !loading.value && !error.value)

  // ===== Actions =====
  /**
   * 从API获取系统配置
   */
  async function fetchConfig() {
    // 如果已经在加载中,避免重复请求
    if (loading.value) {
      return
    }

    loading.value = true
    error.value = null

    try {
      const response = await getSystemConfig()
      
      if (response.code === 200 && response.data) {
        config.value = {
          download_url: response.data.download_url || '',
          live_stream_url_h5: response.data.live_stream_url_h5 || '',
          live_stream_url_web: response.data.live_stream_url_web || ''
        }
        initialized.value = true
        console.log('✅ 系统配置加载成功:', config.value)
      } else {
        throw new Error('配置数据格式错误')
      }
    } catch (err: any) {
      error.value = err.message || '获取系统配置失败'
      console.error('❌ 系统配置加载失败:', err)
      
      // 失败时使用默认配置(兼容旧逻辑)
      config.value = {
        download_url: 'https://download.dbf666.com/',
        live_stream_url_h5: 'https://video.shangkmo.com/?isLive=1&isPreload=1&isAutoplay=1&isRobot=0&isH5=1&islogined=0',
        live_stream_url_web: 'https://video.shangkmo.com/?isLive=1&isRobot=0&isLogined=0&isHideCtrl=0&isNeedDanmaku=1&isForceHls=0&isTwitch=0&isNeedTheaterMode=1'
      }
      initialized.value = true
      console.warn('⚠️ 使用默认配置')
    } finally {
      loading.value = false
    }
  }

  /**
   * 重新加载配置
   */
  function reloadConfig() {
    initialized.value = false
    return fetchConfig()
  }

  /**
   * 重置配置
   */
  function resetConfig() {
    config.value = {
      download_url: '',
      live_stream_url_h5: '',
      live_stream_url_web: ''
    }
    loading.value = false
    error.value = null
    initialized.value = false
  }

  return {
    // State
    config,
    loading,
    error,
    initialized,
    
    // Getters
    downloadUrl,
    liveStreamUrlH5,
    liveStreamUrlWeb,
    isConfigReady,
    
    // Actions
    fetchConfig,
    reloadConfig,
    resetConfig
  }
})

